import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { IonIcon, useIonToast } from '@ionic/react';
import {
  locationOutline, checkmarkCircleOutline, arrowForwardOutline,
  arrowBackOutline, closeOutline, alertCircleOutline, mapOutline,
  createOutline, personOutline,
} from 'ionicons/icons';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import {
  usePendingReview,
  useUpdateFamilyLocation,
  useUpdateMemberLocation,
} from '@/hooks/useMapFamilies';
import { useFamily } from '@/hooks/useFamilies';
import { useMember } from '@/hooks/useMembers';
import type { PendingFamily } from '@/api/maps';
import AddressAutocomplete from '@/components/shared/AddressAutocomplete';
import type { ParsedAddress } from '@/components/shared/AddressAutocomplete';
import { useHistory } from 'react-router-dom';
import { cartoTileUrl } from '@/utils/cartoTiles';

const ARAD_CENTER: [number, number] = [46.1866, 21.3123];
const TILE_URL = cartoTileUrl('rastertiles/voyager');
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>';

const CONFIDENCE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  high:   { label: 'Ridicată',  color: '#059669', bg: '#ecfdf5' },
  medium: { label: 'Medie',     color: '#d97706', bg: '#fffbeb' },
  low:    { label: 'Scăzută',   color: '#dc2626', bg: '#fef2f2' },
  none:   { label: 'Lipsă',     color: '#94a3b8', bg: '#f1f5f9' },
};

/* ── Draggable pin icon ── */
const dragIcon = L.divIcon({
  className: '',
  html: `
    <div class="gpin">
      <div class="gpin__body">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
      <div class="gpin__shadow"></div>
    </div>`,
  iconSize: [36, 48],
  iconAnchor: [18, 48],
});

/* ── Map click handler ── */
const MapClickHandler: React.FC<{ onMapClick: (lat: number, lng: number) => void }> = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

/* ── Fly to position ── */
const FlyTo: React.FC<{ position: [number, number]; zoom?: number }> = ({ position, zoom = 16 }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(position, zoom, { duration: 0.8 });
  }, [map, position, zoom]);
  return null;
};

/* ══════════════════════════════════════════
   GeoReviewPage
   ══════════════════════════════════════════ */

const GeoReviewPage: React.FC = () => {
  const history = useHistory();
  const urlParams = new URLSearchParams(history.location.search);
  const urlSectorId = urlParams.get('sector_id') ?? undefined;
  const urlFamilyId = urlParams.get('family_id') ?? undefined;
  const urlMemberId = urlParams.get('member_id') ?? undefined;

  // Single-entity override mode: when arriving from a map popup's "Corectează poziția"
  // we edit one specific entity even if it's not in the pending-review queue.
  const singleMode: 'family' | 'member' | null = urlMemberId ? 'member' : (urlFamilyId ? 'family' : null);

  const { data: pending, isLoading: loadingPending, refetch } = usePendingReview(
    singleMode ? undefined : urlSectorId
  );
  const { data: familyDetail, isLoading: loadingFamily } = useFamily(
    singleMode === 'family' ? urlFamilyId : undefined
  );
  const { data: memberDetail, isLoading: loadingMember } = useMember(
    singleMode === 'member' ? urlMemberId : undefined
  );

  const updateFamilyLocation = useUpdateFamilyLocation();
  const updateMemberLocation = useUpdateMemberLocation();
  const [presentToast] = useIonToast();

  // Normalize pending families (queue mode) into our generic item shape.
  const queueItems: PendingFamily[] = useMemo(() => pending ?? [], [pending]);

  // In single-entity mode, synthesize a one-item list.
  const singleItem: PendingFamily | null = useMemo(() => {
    if (singleMode === 'family' && familyDetail) {
      return {
        id: familyDetail.id,
        name: familyDetail.name,
        display_name: familyDetail.display_name,
        address: familyDetail.address,
        numar: familyDetail.numar,
        bloc: familyDetail.bloc,
        scara: familyDetail.scara,
        etaj: familyDetail.etaj,
        apartament: familyDetail.apartament,
        city: familyDetail.city,
        sector_id: familyDetail.sector_id,
        geocoding_confidence: null,
        needs_geocoding: familyDetail.needs_geocoding ?? false,
        sector: familyDetail.sector_name
          ? { id: familyDetail.sector_id ?? '', name: familyDetail.sector_name, color: null }
          : null,
      } as PendingFamily;
    }
    if (singleMode === 'member' && memberDetail) {
      return {
        id: memberDetail.id,
        name: memberDetail.full_name,
        display_name: memberDetail.full_name,
        address: memberDetail.address ?? memberDetail.effective_address,
        numar: memberDetail.numar ?? memberDetail.effective_numar,
        bloc: memberDetail.bloc ?? memberDetail.effective_bloc,
        scara: memberDetail.scara ?? memberDetail.effective_scara,
        etaj: memberDetail.etaj ?? memberDetail.effective_etaj,
        apartament: memberDetail.apartament ?? memberDetail.effective_apartament,
        city: memberDetail.city ?? memberDetail.effective_city,
        sector_id: null,
        geocoding_confidence: null,
        needs_geocoding: memberDetail.needs_geocoding ?? false,
        sector: null,
      } as PendingFamily;
    }
    return null;
  }, [singleMode, familyDetail, memberDetail]);

  const families: PendingFamily[] = singleMode
    ? (singleItem ? [singleItem] : [])
    : queueItems;
  const isLoading = singleMode === 'family'
    ? loadingFamily
    : singleMode === 'member'
      ? loadingMember
      : loadingPending;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [pinPosition, setPinPosition] = useState<[number, number] | null>(null);
  const [savedCount, setSavedCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const markerRef = useRef<L.Marker>(null);
  const jumpedToFamily = useRef(false);

  const currentFamily = families[currentIndex] ?? null;
  const totalFamilies = singleMode ? 1 : families.length + savedCount;

  // In single mode, pre-seed the pin with the entity's current coordinates (if any).
  useEffect(() => {
    if (!singleMode) return;
    const lat = singleMode === 'family'
      ? familyDetail?.latitude ?? null
      : (memberDetail?.latitude ?? memberDetail?.effective_latitude ?? null);
    const lng = singleMode === 'family'
      ? familyDetail?.longitude ?? null
      : (memberDetail?.longitude ?? memberDetail?.effective_longitude ?? null);
    if (lat != null && lng != null) {
      setPinPosition([lat, lng]);
    }
  }, [singleMode, familyDetail, memberDetail]);

  // Jump to specific family from URL param (queue mode only)
  useEffect(() => {
    if (singleMode) return;
    if (jumpedToFamily.current || !urlFamilyId || families.length === 0) return;
    const idx = families.findIndex(f => f.id === urlFamilyId);
    if (idx >= 0) {
      setCurrentIndex(idx);
    }
    jumpedToFamily.current = true;
  }, [urlFamilyId, families, singleMode]);

  // Reset pin when switching families
  useEffect(() => {
    setPinPosition(null);
    setJustSaved(false);
  }, [currentIndex]);

  const handleMapClick = useCallback((lat: number, lng: number) => {
    setPinPosition([lat, lng]);
  }, []);

  const handleMarkerDragEnd = useCallback(() => {
    const marker = markerRef.current;
    if (marker) {
      const { lat, lng } = marker.getLatLng();
      setPinPosition([lat, lng]);
    }
  }, []);

  const handleAddressSelect = useCallback((result: ParsedAddress) => {
    setPinPosition([result.latitude, result.longitude]);
  }, []);

  const handleSave = useCallback(async () => {
    if (!currentFamily || !pinPosition) return;
    setSaving(true);
    try {
      if (singleMode === 'member') {
        await updateMemberLocation.mutateAsync({
          memberId: currentFamily.id,
          latitude: pinPosition[0],
          longitude: pinPosition[1],
        });
      } else {
        await updateFamilyLocation.mutateAsync({
          familyId: currentFamily.id,
          latitude: pinPosition[0],
          longitude: pinPosition[1],
        });
      }
      setJustSaved(true);
      setSavedCount(prev => prev + 1);

      if (singleMode) {
        // Brief success flash, then go back to the map.
        setTimeout(() => {
          history.push('/maps');
        }, 700);
        return;
      }

      // Queue mode: auto-advance after brief feedback
      setTimeout(() => {
        if (currentIndex < families.length - 1) {
          setCurrentIndex(prev => prev + 1);
        } else {
          void refetch();
        }
      }, 800);
    } catch {
      // Surface the failure instead of silently leaving the pin "placed" but
      // unsaved — otherwise the correction appears to not stick.
      void presentToast({
        message: 'Nu am putut salva locația. Verifică conexiunea sau permisiunile și încearcă din nou.',
        duration: 4000,
        color: 'danger',
        position: 'bottom',
      });
    } finally {
      setSaving(false);
    }
  }, [
    currentFamily, pinPosition, singleMode,
    updateFamilyLocation, updateMemberLocation,
    currentIndex, families.length, refetch, history, presentToast,
  ]);

  const handleSkip = useCallback(() => {
    if (currentIndex < families.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, families.length]);

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && pinPosition && !saving) {
        e.preventDefault();
        void handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [pinPosition, saving, handleSave]);

  /* ── Empty state ── */
  if (!isLoading && families.length === 0) {
    const emptyTitle = singleMode
      ? 'Entitatea nu a putut fi încărcată'
      : 'Toate adresele sunt verificate';
    const emptyText = singleMode
      ? 'Nu am putut găsi această înregistrare. Te rog întoarce-te la hartă și încearcă din nou.'
      : 'Nu mai sunt familii care necesită plasarea manuală pe hartă.';
    return (
      <div className="mpage">
        <div className="mpage__map">
          <MapContainer center={ARAD_CENTER} zoom={13} style={{ width: '100%', height: '100%' }} scrollWheelZoom zoomControl={false}>
            <TileLayer attribution={TILE_ATTR} url={TILE_URL} subdomains="abcd" maxZoom={20} />
          </MapContainer>
        </div>
        <div className="greview__empty">
          <div className="greview__empty-card">
            <IonIcon icon={checkmarkCircleOutline} className="greview__empty-icon" />
            <h2 className="greview__empty-title">{emptyTitle}</h2>
            <p className="greview__empty-text">{emptyText}</p>
            <button className="greview__btn greview__btn--primary" onClick={() => history.push('/maps')}>
              <IonIcon icon={mapOutline} />
              Înapoi la hartă
            </button>
          </div>
        </div>
      </div>
    );
  }

  const confKey = currentFamily?.geocoding_confidence || 'none';
  const conf = (CONFIDENCE_CONFIG[confKey] ?? CONFIDENCE_CONFIG['none'])!;

  return (
    <div className="mpage">
      {/* ── Full screen map ── */}
      <div className="mpage__map">
        {isLoading ? (
          <div className="mpage__loading">
            <div className="mpage__spinner" />
            <span>Se încarcă...</span>
          </div>
        ) : (
          <MapContainer
            center={pinPosition ?? ARAD_CENTER}
            zoom={pinPosition ? 17 : 15}
            style={{ width: '100%', height: '100%' }}
            scrollWheelZoom
            zoomControl={false}
          >
            <TileLayer attribution={TILE_ATTR} url={TILE_URL} subdomains="abcd" maxZoom={20} />
            <MapClickHandler onMapClick={handleMapClick} />
            {pinPosition && <FlyTo position={pinPosition} />}
            {pinPosition && (
              <Marker
                ref={markerRef}
                position={pinPosition}
                icon={dragIcon}
                draggable
                eventHandlers={{ dragend: handleMarkerDragEnd }}
              />
            )}
          </MapContainer>
        )}
      </div>

      {/* ── Progress bar (top) — queue mode only ── */}
      {!singleMode && (
        <div className="greview__progress">
          <div className="greview__progress-bar">
            <div
              className="greview__progress-fill"
              style={{ width: totalFamilies > 0 ? `${(savedCount / totalFamilies) * 100}%` : '0%' }}
            />
          </div>
          <span className="greview__progress-text">
            {savedCount} din {totalFamilies} revizuite
          </span>
        </div>
      )}

      {/* ── Single-mode banner ── */}
      {singleMode && currentFamily && (
        <div className="greview__mode-banner">
          <span className="greview__mode-banner-icon">
            <IonIcon icon={singleMode === 'member' ? personOutline : createOutline} />
          </span>
          <span className="greview__mode-banner-text">
            Ajustare manuală
            <span className="greview__mode-banner-sub">
              {singleMode === 'member' ? 'persoană individuală' : 'familie'}
            </span>
          </span>
        </div>
      )}

      {/* ── Back to map ── */}
      <button className="greview__back-btn" onClick={() => history.push('/maps')} title="Înapoi la hartă">
        <IonIcon icon={arrowBackOutline} />
      </button>

      {/* ── Bottom panel ── */}
      {currentFamily && (
        <div className={`greview__panel${justSaved ? ' greview__panel--saved' : ''}`}>
          {/* Family info header */}
          <div className="greview__family">
            <div className="greview__family-main">
              <h3 className="greview__family-name">{currentFamily.display_name || currentFamily.name}</h3>
              <div className="greview__family-address">
                <IonIcon icon={locationOutline} />
                <span>
                  {[
                    currentFamily.address,
                    currentFamily.numar && `Nr. ${currentFamily.numar}`,
                    currentFamily.bloc && `Bl. ${currentFamily.bloc}`,
                    currentFamily.scara && `Sc. ${currentFamily.scara}`,
                    currentFamily.etaj && `Et. ${currentFamily.etaj}`,
                    currentFamily.apartament && `Ap. ${currentFamily.apartament}`,
                    currentFamily.city,
                  ].filter(Boolean).join(', ')}
                </span>
              </div>
              {currentFamily.sector && (
                <span className="greview__family-sector" style={{ borderColor: currentFamily.sector.color || '#e2e8f0' }}>
                  <span className="greview__sector-dot" style={{ background: currentFamily.sector.color || '#94a3b8' }} />
                  {currentFamily.sector.name}
                </span>
              )}
            </div>
            <div className="greview__family-meta">
              {!singleMode && (
                <span className="greview__confidence" style={{ background: conf.bg, color: conf.color }}>
                  <IonIcon icon={alertCircleOutline} />
                  {conf.label}
                </span>
              )}
              {!singleMode && (
                <span className="greview__nav-count">
                  {currentIndex + 1} / {families.length}
                </span>
              )}
            </div>
          </div>

          {/* Search bar */}
          <div className="greview__search">
            <AddressAutocomplete
              value=""
              placeholder="Caută adresa pe hartă..."
              onSelect={handleAddressSelect}
              className="greview__autocomplete"
            />
          </div>

          {/* Instructions / pin info */}
          <div className="greview__hint">
            {!pinPosition ? (
              <span className="greview__hint-text">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                Click pe hartă sau caută adresa pentru a plasa pinul
              </span>
            ) : (
              <span className="greview__hint-text greview__hint-text--placed">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {pinPosition[0].toFixed(5)}, {pinPosition[1].toFixed(5)}
                <button className="greview__hint-clear" onClick={() => setPinPosition(null)}>
                  <IonIcon icon={closeOutline} />
                </button>
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="greview__actions">
            {singleMode ? (
              <button
                className="greview__btn greview__btn--ghost"
                onClick={() => history.push('/maps')}
              >
                <IonIcon icon={arrowBackOutline} />
                Anulează
              </button>
            ) : (
              <>
                <button
                  className="greview__btn greview__btn--ghost"
                  onClick={handleBack}
                  disabled={currentIndex === 0}
                >
                  <IonIcon icon={arrowBackOutline} />
                  Înapoi
                </button>
                <button
                  className="greview__btn greview__btn--ghost"
                  onClick={handleSkip}
                  disabled={currentIndex >= families.length - 1}
                >
                  Sari peste
                  <IonIcon icon={arrowForwardOutline} />
                </button>
              </>
            )}
            <button
              className="greview__btn greview__btn--primary"
              onClick={handleSave}
              disabled={!pinPosition || saving}
            >
              {saving ? (
                <div className="mpage__spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
              ) : justSaved ? (
                <><IonIcon icon={checkmarkCircleOutline} /> Salvat!</>
              ) : (
                <>{singleMode ? 'Salvează poziția' : 'Salvează locația'}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Queue sidebar (right) — queue mode only ── */}
      {!singleMode && families.length > 1 && (
        <div className="greview__queue">
          <div className="greview__queue-title">Coadă de revizuire</div>
          <div className="greview__queue-list">
            {families.map((f, i) => (
              <button
                key={f.id}
                className={`greview__queue-item${i === currentIndex ? ' greview__queue-item--active' : ''}`}
                onClick={() => setCurrentIndex(i)}
              >
                <span className="greview__queue-name">{f.display_name || f.name}</span>
                <span className="greview__queue-addr">
                  {[f.address, f.numar && `Nr.${f.numar}`, f.bloc && `Bl.${f.bloc}`, f.city].filter(Boolean).join(', ')}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeoReviewPage;
